/**
 * The arena as rules rather than pixels: the field, the body, the apple, and
 * what the planner can see of them.
 *
 * Pure on purpose - the Game view runs one of these steps per Arc block, and
 * everything the move depends on (features, reachable space, death) is checked
 * without a canvas. Nothing here knows about blocks, JEV or the DOM.
 */

import { mulberry32 } from "../random";

export type Point = { readonly x: number; readonly y: number };

export type Direction = "up" | "down" | "left" | "right";

/** The field is scanned in this order, and ties are broken in it. */
export const DIRECTIONS = ["up", "down", "left", "right"] as const satisfies readonly Direction[];

export type Arena = {
  readonly size: number;
  readonly seed: number;
  /** Head first, tail last, one cell per step. */
  readonly body: readonly Point[];
  readonly food: Point;
  readonly score: number;
  readonly eaten: number;
  readonly dead: boolean;
};

export type Stepped = {
  readonly arena: Arena;
  readonly ate: boolean;
  readonly died: boolean;
};

export type Features = {
  /** The apple relative to the head, in cells. */
  readonly food: { readonly dx: number; readonly dy: number };
  /** Toward the apple, dominant axis first. */
  readonly foodDirections: readonly Direction[];
  /** Directions that walk into a wall or the body's own flesh. */
  readonly danger: readonly Direction[];
  readonly safe: readonly Direction[];
  /** Free cells the head could still reach. */
  readonly freeSpace: number;
  readonly freeSpaceRatio: number;
  readonly length: number;
  readonly cells: number;
};

/** One cell per direction, in the field's coordinates (y grows downward). */
export const STEPS: Readonly<Record<Direction, Point>> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

function cellKey(cell: Point): string {
  return `${cell.x},${cell.y}`;
}

function moved(cell: Point, direction: Direction): Point {
  return { x: cell.x + STEPS[direction].x, y: cell.y + STEPS[direction].y };
}

function onField(arena: Arena, cell: Point): boolean {
  return cell.x >= 0 && cell.y >= 0 && cell.x < arena.size && cell.y < arena.size;
}

export function head(arena: Arena): Point {
  const first = arena.body[0];

  if (first === undefined) throw new Error("an arena always has a head");

  return first;
}

export function createArena(input: { readonly size: number; readonly seed: number }): Arena {
  const middle = Math.floor(input.size / 2);

  const body: Point[] = [
    { x: middle, y: middle },
    { x: middle, y: middle + 1 },
    { x: middle, y: middle + 2 },
  ];

  return {
    size: input.size,
    seed: input.seed,
    body,
    food: spawnFood(input.seed, 0, body, input.size),
    score: 0,
    eaten: 0,
    dead: false,
  };
}

/**
 * The apple's cell, derived from the seed and how many have been eaten: the
 * same arena always grows the same apples, so the same run always meets the
 * same board.
 */
function spawnFood(seed: number, eaten: number, body: readonly Point[], size: number): Point {
  const taken = new Set(body.map(cellKey));
  const free: Point[] = [];

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      if (!taken.has(cellKey({ x, y }))) free.push({ x, y });
    }
  }

  const random = mulberry32((seed + eaten * 0x9e3779b9) >>> 0);
  const picked = free[Math.floor(random() * free.length)];

  if (picked === undefined) throw new Error("an arena always has a free cell for the apple");

  return picked;
}

/**
 * The body blocks a step, except its last cell: the tail leaves as the head
 * arrives, which is the rule every snake player knows.
 */
export function wouldDie(arena: Arena, direction: Direction): boolean {
  const next = moved(head(arena), direction);

  if (!onField(arena, next)) return true;

  return arena.body.slice(0, -1).some((cell) => cell.x === next.x && cell.y === next.y);
}

export function step(arena: Arena, direction: Direction): Stepped {
  if (arena.dead) return { arena, ate: false, died: false };

  if (wouldDie(arena, direction)) {
    return { arena: { ...arena, dead: true }, ate: false, died: true };
  }

  const next = moved(head(arena), direction);
  const ate = next.x === arena.food.x && next.y === arena.food.y;
  const stretched = [next, ...arena.body];
  const body = ate ? stretched : stretched.slice(0, -1);
  const eaten = arena.eaten + (ate ? 1 : 0);

  return {
    arena: {
      ...arena,
      body,
      food: ate ? spawnFood(arena.seed, eaten, body, arena.size) : arena.food,
      score: arena.score + (ate ? 1 : 0),
      eaten,
    },
    ate,
    died: false,
  };
}

/**
 * Free cells reachable from a cell, flooding the field around the body. The
 * tail is not an obstacle (it moves), the head counts as occupied: what is
 * measured is room to keep playing, not room to stand.
 */
export function freeSpace(arena: Arena, from: Point): number {
  const blocked = new Set(arena.body.slice(0, -1).map(cellKey));
  const seen = new Set<string>([cellKey(from)]);
  const queue: Point[] = [from];
  let free = 0;

  for (let at = 0; at < queue.length; at += 1) {
    const cell = queue[at];

    if (!blocked.has(cellKey(cell))) free += 1;

    for (const direction of DIRECTIONS) {
      const next = moved(cell, direction);

      if (!onField(arena, next)) continue;

      const key = cellKey(next);

      if (seen.has(key) || blocked.has(key)) continue;

      seen.add(key);
      queue.push(next);
    }
  }

  return free;
}

/** The room left behind by a step, measured from where the head lands. */
export function freeSpaceAfter(arena: Arena, direction: Direction): number {
  const next = step(arena, direction);

  if (next.died) return 0;

  return freeSpace(next.arena, head(next.arena));
}

export function features(arena: Arena): Features {
  const danger = DIRECTIONS.filter((direction) => wouldDie(arena, direction));
  const safe = DIRECTIONS.filter((direction) => !wouldDie(arena, direction));
  const own = head(arena);
  const food = { dx: arena.food.x - own.x, dy: arena.food.y - own.y };
  const room = freeSpace(arena, own);
  const cells = arena.size * arena.size;

  return {
    food,
    foodDirections: directionsToward(food),
    danger,
    safe,
    freeSpace: room,
    freeSpaceRatio: room / cells,
    length: arena.body.length,
    cells,
  };
}

/** Toward the apple on the dominant axis first, then the other one. */
function directionsToward(food: { readonly dx: number; readonly dy: number }): Direction[] {
  const vertical: Direction = food.dy < 0 ? "up" : "down";
  const horizontal: Direction = food.dx < 0 ? "left" : "right";
  const down = Math.abs(food.dy);
  const across = Math.abs(food.dx);
  const order: Direction[] = [];

  if (down >= across && down > 0) {
    order.push(vertical);

    if (across > 0) order.push(horizontal);
  } else if (across > 0) {
    order.push(horizontal);

    if (down > 0) order.push(vertical);
  }

  return order;
}
